/* eslint-disable max-classes-per-file */
import { IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { IDagKvantitet } from "~app/common/models/dagkvantitet";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { LeveransraderAggregat } from "~app/common/models/leveransrad";
import { TaktCalculator } from "~app/common/models/takt-calculator";
import { IOrderLeverantorsorderrad } from "./justeraLeveransplanModel";

export class LeverantorsorderAggregat {
    leverantorsorderrader: LeverantorsorderradAggregat[];

    constructor(raw: IOrderLeverantorsorderrad[]) {
        this.leverantorsorderrader = raw.map(x => new LeverantorsorderradAggregat(x));
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.leverantorsorderrader.reduce((agg, item) => agg + item.sumLeveransrad(period), 0);

    sumTransporterat = (period: IAggregatPeriod): number => this.leverantorsorderrader.reduce((agg, item) => agg + item.sumTransporterat(period), 0);

    sumTransporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumLeveransrad(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    sumPlan = (period: IAggregatPeriod): number => this.leverantorsorderrader.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumBalans = (period: IAggregatPeriod): number => this.leverantorsorderrader.reduce((agg, item) => agg + item.sumBalans(period), 0);

    sumOverenskommet = (): number => this.leverantorsorderrader.reduce((agg, item) => agg + item.sumOverenskommet(), 0);

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    calculateTaktLeverans = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumLeveransrad, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);
}

export class LeverantorsorderradAggregat {
    readonly forsorjningskallor: ForsorjningskallaAggregat[];
    private readonly leveransraderAggregat: LeveransraderAggregat;

    constructor(private readonly raw: IOrderLeverantorsorderrad) {
        this.leveransraderAggregat = new LeveransraderAggregat(raw.leverantorsorderrad.levorderLeveransrader);
        this.forsorjningskallor = ForsorjningskallaAggregat.createMany(raw.leverantorsorderrad.forsorjningskallor, this.leveransraderAggregat);
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumLeveransrad(period);

    sumBalans = (period: IAggregatPeriod): number => {
        if (!period.isCurrentlyActive) {
            return this.sumTransporterat(period) - this.sumLeveransrad(period);
        }

        return this.sumPlan(period) - this.sumLeveransrad(period);
    };

    sumTransporterat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumTransporterat(period);

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningskallor.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumOverenskommet = (): number => this.forsorjningskallor.reduce((agg, item) => agg + item.overenskommet, 0);

    transporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumPlan(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    avvikelse = (periods: IAggregatPeriod[]): number => this.transporteratLeveransrad(periods) - this.sumOverenskommet();

    get periodFrom() {
        return this.raw.leverantorsorderrad.periodFrom;
    }

    get periodTom() {
        return this.raw.leverantorsorderrad.periodTom;
    }

    calculateTaktLeverans = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumLeveransrad, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    get leverantorsorderradId(): number {
        return this.raw.leverantorsorderrad.id;
    }

    get id(): number {
        return this.raw.id;
    }

    get leverantor(): string {
        return this.raw.leverantor.split(",")[0];
    }

    get leverantorsNummer(): string {
        return this.raw.leverantor.split(",")[1];
    }

    get handelssortimentsGrupp(): string {
        return this.raw.leverantorsorderrad.handelssortimentsgrupp.namn;
    }

    get mattslag(): string {
        return this.raw.leverantorsorderrad.mattslag;
    }

    updateLeveransradKvantitet = (fordeladeKvantiteter: IDagKvantitet[]): void => {
        this.leveransraderAggregat.updateLeveransradKvantitet(fordeladeKvantiteter);
    };
}

export class JlpLeverantorsorderSearchModel {
    kunder?: any[];
    omraden?: any[];
    mottagningsplatser?: any[];
    handelssortimentsgrupper?: any[];
    periodFrom: Date;
    periodTom: Date;
    avtalsansvariga: any[];
    leverantorer: any[];

    constructor() {
        this.kunder = [];
        this.omraden = [];
        this.mottagningsplatser = [];
        this.handelssortimentsgrupper = [];
        this.periodFrom = null;
        this.periodTom = null;
        this.avtalsansvariga = [];
        this.leverantorer = [];
    }
}
